/**
 * CRM Member Scheduling Types
 *
 * Types for managing member work schedules, breaks, and availability
 */

/**
 * Time slot in HH:mm format
 */
export interface MemberTimeSlot {
  start: string // "09:00"
  end: string // "18:00"
}

/**
 * Day of week schedule
 */
export interface MemberDaySchedule {
  dayOfWeek: number // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  isWorkDay: boolean
  shifts: MemberTimeSlot[]
}

/**
 * Weekly schedule configuration
 */
export interface MemberWeeklySchedule {
  monday: MemberDaySchedule
  tuesday: MemberDaySchedule
  wednesday: MemberDaySchedule
  thursday: MemberDaySchedule
  friday: MemberDaySchedule
  saturday: MemberDaySchedule
  sunday: MemberDaySchedule
}

/**
 * Break type
 */
export type CrmBreakType = 'lunch' | 'coffee' | 'other'

/**
 * Break schedule
 */
export interface CrmBreakSchedule {
  id: string
  name: string // "Lunch", "Coffee break"
  type: CrmBreakType
  startTime: string // "12:00"
  endTime: string // "13:00"
  daysOfWeek: number[] // [1, 2, 3, 4, 5] for weekdays
}

/**
 * Schedule exception types
 */
export type CrmScheduleExceptionType =
  | 'vacation' // Vacation/PTO
  | 'sick_leave' // Sick leave
  | 'training' // Training/education
  | 'meeting' // Meetings
  | 'other' // Other exceptions

/**
 * Schedule exception (vacation, sick leave, etc.)
 */
export interface CrmScheduleException {
  id: string
  memberId: string
  type: CrmScheduleExceptionType
  startDate: string // ISO date
  endDate: string // ISO date
  reason?: string
  isApproved: boolean
  approvedBy?: string
  approvedAt?: string
  createdAt: string
}

/**
 * Complete member schedule
 */
export interface CrmMemberSchedule {
  id: string
  memberId: string
  weeklySchedule: MemberWeeklySchedule
  breaks: CrmBreakSchedule[]
  exceptions: CrmScheduleException[]
  timezone: string // "Europe/Moscow", "America/New_York"
  effectiveFrom: string // ISO date
  effectiveTo?: string // ISO date
  createdAt: string
  updatedAt: string
  createdBy: string
}

/**
 * Schedule template for common schedules
 */
export interface CrmScheduleTemplate {
  id: string
  name: string // "9-5 Weekdays", "24/7 Shifts"
  description?: string
  weeklySchedule: MemberWeeklySchedule
  breaks: CrmBreakSchedule[]
  isDefault?: boolean
}

/**
 * Member availability result
 */
export interface CrmMemberAvailability {
  memberId: string
  isAvailable: boolean
  currentStatus: 'available' | 'on_break' | 'off_shift' | 'exception'
  nextAvailableAt?: string // ISO datetime
  currentException?: CrmScheduleException
}

/**
 * Create default 9-5 weekday schedule
 */
export function createDefaultWeekdaySchedule(): MemberWeeklySchedule {
  const workDay: MemberDaySchedule = {
    dayOfWeek: 1, // Will be overridden
    isWorkDay: true,
    shifts: [{ start: '09:00', end: '18:00' }],
  }

  const weekendDay: MemberDaySchedule = {
    dayOfWeek: 0, // Will be overridden
    isWorkDay: false,
    shifts: [],
  }

  return {
    monday: { ...workDay, dayOfWeek: 1 },
    tuesday: { ...workDay, dayOfWeek: 2 },
    wednesday: { ...workDay, dayOfWeek: 3 },
    thursday: { ...workDay, dayOfWeek: 4 },
    friday: { ...workDay, dayOfWeek: 5 },
    saturday: { ...weekendDay, dayOfWeek: 6 },
    sunday: { ...weekendDay, dayOfWeek: 0 },
  }
}

/**
 * Create 24/7 schedule
 */
export function create247Schedule(): MemberWeeklySchedule {
  const allDayShift: MemberDaySchedule = {
    dayOfWeek: 0, // Will be overridden
    isWorkDay: true,
    shifts: [{ start: '00:00', end: '23:59' }],
  }

  return {
    sunday: { ...allDayShift, dayOfWeek: 0 },
    monday: { ...allDayShift, dayOfWeek: 1 },
    tuesday: { ...allDayShift, dayOfWeek: 2 },
    wednesday: { ...allDayShift, dayOfWeek: 3 },
    thursday: { ...allDayShift, dayOfWeek: 4 },
    friday: { ...allDayShift, dayOfWeek: 5 },
    saturday: { ...allDayShift, dayOfWeek: 6 },
  }
}

/**
 * Get day schedule by day of week
 */
export function getDaySchedule(
  weeklySchedule: MemberWeeklySchedule,
  dayOfWeek: number
): MemberDaySchedule {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const
  const dayKey = days[dayOfWeek] ?? 'sunday'
  return weeklySchedule[dayKey]
}

/**
 * Check if time is within a time slot
 */
export function isTimeInSlot(time: string, slot: MemberTimeSlot): boolean {
  return time >= slot.start && time <= slot.end
}

/**
 * Check if member is available at given date/time
 */
export function isMemberAvailable(
  schedule: CrmMemberSchedule,
  date: Date
): CrmMemberAvailability {
  const dayOfWeek = date.getDay()
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  const dateString = date.toISOString().split('T')[0] ?? ''

  // Check for exceptions first
  const activeException = schedule.exceptions.find(
    (exc) => exc.isApproved && dateString >= exc.startDate && dateString <= exc.endDate
  )

  if (activeException) {
    return {
      memberId: schedule.memberId,
      isAvailable: false,
      currentStatus: 'exception',
      currentException: activeException,
      nextAvailableAt: new Date(activeException.endDate + 'T23:59:59').toISOString(),
    }
  }

  // Get day schedule
  const daySchedule = getDaySchedule(schedule.weeklySchedule, dayOfWeek)

  if (!daySchedule.isWorkDay) {
    return {
      memberId: schedule.memberId,
      isAvailable: false,
      currentStatus: 'off_shift',
    }
  }

  // Check if in work shift
  const isInShift = daySchedule.shifts.some((shift) => isTimeInSlot(time, shift))

  if (!isInShift) {
    return {
      memberId: schedule.memberId,
      isAvailable: false,
      currentStatus: 'off_shift',
    }
  }

  // Check if on break
  const activeBreak = schedule.breaks.find(
    (brk) => brk.daysOfWeek.includes(dayOfWeek) && isTimeInSlot(time, { start: brk.startTime, end: brk.endTime })
  )

  if (activeBreak) {
    const [hours, minutes] = activeBreak.endTime.split(':')
    return {
      memberId: schedule.memberId,
      isAvailable: false,
      currentStatus: 'on_break',
      nextAvailableAt: new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        parseInt(hours ?? '0'),
        parseInt(minutes ?? '0')
      ).toISOString(),
    }
  }

  return {
    memberId: schedule.memberId,
    isAvailable: true,
    currentStatus: 'available',
  }
}

/**
 * Default schedule templates
 */
export const DEFAULT_SCHEDULE_TEMPLATES: CrmScheduleTemplate[] = [
  {
    id: 'template-9-5-weekdays',
    name: '9-5 Weekdays',
    description: 'Standard office hours, Monday to Friday, 9am to 5pm',
    weeklySchedule: createDefaultWeekdaySchedule(),
    breaks: [
      {
        id: 'lunch',
        name: 'Lunch Break',
        type: 'lunch',
        startTime: '12:00',
        endTime: '13:00',
        daysOfWeek: [1, 2, 3, 4, 5],
      },
    ],
    isDefault: true,
  },
  {
    id: 'template-247',
    name: '24/7 Coverage',
    description: 'Full coverage, all days, all hours',
    weeklySchedule: create247Schedule(),
    breaks: [],
  },
  {
    id: 'template-morning-shift',
    name: 'Morning Shift',
    description: '6am to 2pm, Monday to Friday',
    weeklySchedule: {
      ...createDefaultWeekdaySchedule(),
      monday: { dayOfWeek: 1, isWorkDay: true, shifts: [{ start: '06:00', end: '14:00' }] },
      tuesday: { dayOfWeek: 2, isWorkDay: true, shifts: [{ start: '06:00', end: '14:00' }] },
      wednesday: { dayOfWeek: 3, isWorkDay: true, shifts: [{ start: '06:00', end: '14:00' }] },
      thursday: { dayOfWeek: 4, isWorkDay: true, shifts: [{ start: '06:00', end: '14:00' }] },
      friday: { dayOfWeek: 5, isWorkDay: true, shifts: [{ start: '06:00', end: '14:00' }] },
    },
    breaks: [
      {
        id: 'break',
        name: 'Break',
        type: 'coffee',
        startTime: '10:00',
        endTime: '10:15',
        daysOfWeek: [1, 2, 3, 4, 5],
      },
    ],
  },
  {
    id: 'template-evening-shift',
    name: 'Evening Shift',
    description: '2pm to 10pm, Monday to Friday',
    weeklySchedule: {
      ...createDefaultWeekdaySchedule(),
      monday: { dayOfWeek: 1, isWorkDay: true, shifts: [{ start: '14:00', end: '22:00' }] },
      tuesday: { dayOfWeek: 2, isWorkDay: true, shifts: [{ start: '14:00', end: '22:00' }] },
      wednesday: { dayOfWeek: 3, isWorkDay: true, shifts: [{ start: '14:00', end: '22:00' }] },
      thursday: { dayOfWeek: 4, isWorkDay: true, shifts: [{ start: '14:00', end: '22:00' }] },
      friday: { dayOfWeek: 5, isWorkDay: true, shifts: [{ start: '14:00', end: '22:00' }] },
    },
    breaks: [
      {
        id: 'dinner',
        name: 'Dinner Break',
        type: 'lunch',
        startTime: '18:00',
        endTime: '18:30',
        daysOfWeek: [1, 2, 3, 4, 5],
      },
    ],
  },
]
